import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { OrgChart } from "@/components/OrgChart";
import { useAuth } from "@/hooks/useAuth";
import { Loader2 } from "lucide-react";

export interface EmployeeWithProfile {
  id: string;
  name: string;
  position: string;
  department: string | null;
  direct_manager_id: string | null;
  profile_image_url: string | null;
  job_description: string | null;
  employee_number: string | null;
  linkedUser?: {
    id: string;
    email: string;
    full_name: string | null;
  } | null;
}

const OrgChartPage = () => {
  const [employees, setEmployees] = useState<EmployeeWithProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, userRole } = useAuth();

  useEffect(() => {
    if (user) {
      loadEmployeesWithProfiles();
    }
  }, [user]);

  const loadEmployeesWithProfiles = async () => {
    try {
      // All authenticated users with proper permissions can access employees
      const { data, error } = await supabase
        .from("employees")
        .select("id, name, position, department, direct_manager_id, profile_image_url, job_description, employee_number")
        .order("name");

      if (error) throw error;
      const employeesData = data || [];

      // Load profiles to find linked users
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select("id, email, full_name, employee_id");

      if (profilesError) throw profilesError;

      // Merge data
      const employeesWithProfiles = employeesData.map(emp => {
        const linkedProfile = profilesData?.find(p => p.employee_id === emp.id);
        return {
          ...emp,
          linkedUser: linkedProfile ? {
            id: linkedProfile.id,
            email: linkedProfile.email,
            full_name: linkedProfile.full_name
          } : null
        };
      });

      setEmployees(employeesWithProfiles);
    } catch (error) {
      console.error("Error loading employees:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Organigramm</h1>
        <p className="text-muted-foreground">
          Organisationsstruktur und Mitarbeiter-Übersicht
        </p>
      </div>

      <OrgChart 
        employees={employees} 
        onEmployeeUpdate={loadEmployeesWithProfiles}
      />
    </div>
  );
};

export default OrgChartPage;
